import type { FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddressInput } from "@/shared/components/ui/address-input";
import { currencyInput } from "@/shared/utils/currencyInput";
import type { EstateItem } from "@/shared/types/estateTypes";

export interface MortgageFormValues {
  propertyAddress: string;
  estimatedValue: string;
  lenderName: string;
  remainingBalance: string;
  monthlyPayment: string;
  statementFile: File | null;
  contractFile: File | null;
}

export const EMPTY_MORTGAGE_FORM: MortgageFormValues = {
  propertyAddress: "",
  estimatedValue: "",
  lenderName: "",
  remainingBalance: "",
  monthlyPayment: "",
  statementFile: null,
  contractFile: null,
};

export interface MortgageFieldErrors {
  propertyAddress?: string;
}

export function validateMortgageFields(form: MortgageFormValues): MortgageFieldErrors {
  if (!form.propertyAddress.trim()) return { propertyAddress: "Please enter a property address" };
  return {};
}

export function mortgageToFormValues(item: EstateItem): MortgageFormValues {
  const meta = (item.metadata ?? {}) as Record<string, unknown>;
  return {
    propertyAddress: (meta.property_address as string) || "",
    lenderName: (meta.lender_name as string) || "",
    remainingBalance: item.balance ? String(item.balance) : "",
    monthlyPayment: meta.monthly_payment ? String(meta.monthly_payment) : "",
    estimatedValue: meta.estimated_value ? String(meta.estimated_value) : "",
    statementFile: null,
    contractFile: null,
  };
}

export function mortgageToItemFields(form: MortgageFormValues) {
  return {
    item_name: form.lenderName.trim() || "Mortgage",
    balance: parseFloat(form.remainingBalance) || 0,
    metadata: {
      property_address: form.propertyAddress.trim() || undefined,
      lender_name: form.lenderName.trim() || undefined,
      monthly_payment: parseFloat(form.monthlyPayment) || undefined,
      estimated_value: parseFloat(form.estimatedValue) || undefined,
    },
  };
}

interface MortgageFormProps {
  form: MortgageFormValues;
  onChange: (form: MortgageFormValues) => void;
  fieldErrors?: MortgageFieldErrors;
  showEstimatedValue?: boolean;
  portalContainer?: HTMLElement | null;
}

export const MortgageForm: FC<MortgageFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showEstimatedValue = false,
  portalContainer,
}) => {
  const remainingBalanceInput = currencyInput(form.remainingBalance, (val) =>
    onChange({ ...form, remainingBalance: val }),
  );
  const monthlyPaymentInput = currencyInput(form.monthlyPayment, (val) =>
    onChange({ ...form, monthlyPayment: val }),
  );
  const estimatedValueInput = currencyInput(form.estimatedValue, (val) =>
    onChange({ ...form, estimatedValue: val }),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">Property address</Label>
        <AddressInput
          value={form.propertyAddress}
          onChange={(value) => onChange({ ...form, propertyAddress: value })}
          placeholder="e.g., 458 Oak Street, Chicago, IL, 12345"
          portalContainer={portalContainer}
          className={fieldErrors?.propertyAddress ? "border-error" : ""}
        />
        <FieldError message={fieldErrors?.propertyAddress} className="mt-1" />
      </div>

      {showEstimatedValue && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Estimated value <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={estimatedValueInput.displayValue}
            onChange={estimatedValueInput.handleChange}
            placeholder="0.00"
          />
        </div>
      )}

      <div className="border-t border-primary" />

      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Lender name <span className="text-tertiary">(optional)</span>
        </Label>
        <Input
          value={form.lenderName}
          onChange={(e) => onChange({ ...form, lenderName: e.target.value })}
          placeholder="e.g., Wells Fargo, Chase, Bank of America"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Remaining balance <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={remainingBalanceInput.displayValue}
            onChange={remainingBalanceInput.handleChange}
            placeholder="0.00"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Monthly payment <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={monthlyPaymentInput.displayValue}
            onChange={monthlyPaymentInput.handleChange}
            placeholder="0.00"
          />
        </div>
      </div>

      <UploadZone
        label="Recent mortgage statement"
        labelSuffix="(optional)"
        file={form.statementFile}
        onFileSelect={(file) => onChange({ ...form, statementFile: file })}
        onFileRemove={() => onChange({ ...form, statementFile: null })}
      />

      <UploadZone
        label="Original mortgage contract"
        labelSuffix="(optional)"
        file={form.contractFile}
        onFileSelect={(file) => onChange({ ...form, contractFile: file })}
        onFileRemove={() => onChange({ ...form, contractFile: null })}
      />
    </div>
  );
};
